import SearchIcon from "@mui/icons-material/Search";
import { Box, InputAdornment } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CONTROL_HEIGHT_PX, FONT_SIZE, INK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { FilterPillButton } from "../../UIToolkit/Filters/FilterPillButton";
import { filterSearchFieldProps } from "../../UIToolkit/Inputs/filterSearchFieldProps";
import { SystemValidationTextField } from "../../UIToolkit/Inputs/SystemValidationTextField";
import type { GapsDayFilter } from "./gapsDayModel";

type Props = {
	searchTerm: string;
	onSearchChange: (next: string) => void;
	filter: GapsDayFilter;
	isNarrowed: boolean;
	onOpenFilter: () => void;
	/** Shows the scope but will not open it — for a tab the scope does not apply to. */
	isFilterDisabled: boolean;
	/** Keeps the term but takes no input — for a tab with no names to search (the Posted tab's open shifts). */
	isSearchDisabled: boolean;
};

/**
 * The artifact's dense search row (`.sfield`, ~34px): the app's `COMPACT` control height, since this row
 * is pinned chrome that has to leave the list its room, not a form field.
 */
const SEARCH_FIELD_HEIGHT = CONTROL_HEIGHT_PX.COMPACT;

/**
 * Search, and the filter behind it, in one pinned row.
 *
 * The filter button names the scope it is holding — `Nurse · 3 units` — rather than just "Filter", so a
 * supervisor can tell at a glance that the numbers above are theirs and not the building's, without
 * opening anything. Everything shows everything: the plain label is the unfiltered state.
 */
export const GapsSearchRow: FC<Props> = ({ searchTerm, onSearchChange, filter, isNarrowed, onOpenFilter, isFilterDisabled, isSearchDisabled }) => {
	const { t } = useTranslation();

	const filterLabel = useMemo<string>(() => {
		const parts: string[] = [];
		if (filter.jobLabels !== undefined) {
			parts.push(filter.jobLabels.size === 1 ? Array.from(filter.jobLabels)[0] : t("gaps.filter.summaryJobs", { count: filter.jobLabels.size }));
		}
		if (filter.locationNames !== undefined) parts.push(t("gaps.filter.summaryUnits", { count: filter.locationNames.size }));
		if (filter.timeLabels !== undefined) parts.push(t("gaps.filter.summaryShifts", { count: filter.timeLabels.size }));
		return parts.length > 0 ? parts.join(" · ") : t("gaps.filter.title");
	}, [filter, t]);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.875, px: 1.375, py: 0.875, bgcolor: SURFACE, borderTop: `1px solid ${NEUTRAL[150]}` }}>
			<SystemValidationTextField
				value={searchTerm}
				onChange={(event) => onSearchChange(event.target.value)}
				onClear={searchTerm ? () => onSearchChange("") : undefined}
				placeholder={t("search.staff")}
				disabled={isSearchDisabled}
				reserveHeightForHelperText={false}
				{...filterSearchFieldProps("gaps-staff-search")}
				fullWidth
				formControlSx={{ flex: 1, minWidth: 0 }}
				// `inputSx` lands on the TextField root, so the box itself is reached through its outlined input.
				inputSx={{ "& .MuiOutlinedInput-root": { height: SEARCH_FIELD_HEIGHT, fontSize: FONT_SIZE.SECONDARY, bgcolor: NEUTRAL[50] } }}
				inputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<SearchIcon sx={{ fontSize: 16, color: INK.MUTED }} />
						</InputAdornment>
					),
				}}
				dataTestId="gaps-search-input"
			/>
			{/* Names the scope it holds, so the label is also its accessible name; a long scope ellipsizes at 45%. */}
			<FilterPillButton
				label={filterLabel}
				isActive={isNarrowed}
				onClick={onOpenFilter}
				disabled={isFilterDisabled}
				// Pinned chrome on a phone: the pill reaches 44px on touch.
				touchTarget
				dataTestId="gaps-filter-open"
				sx={{ maxWidth: "45%" }}
			/>
		</Box>
	);
};
